<template>
  <div class="abroad-date-range-picker" ref="root" :style="rootStyle">
    <div
      class="adrp-trigger"
      :class="{ 'is-active': visible }"
      :title="triggerTitle"
      @click="togglePanel"
    >
      <i class="el-icon-date adrp-trigger__icon"></i>
      <span v-if="!hasValue" class="adrp-trigger__placeholder">{{ t('placeholder') }}</span>
      <template v-else>
        <span class="adrp-trigger__date">{{ value[0] }}</span>
        <span class="adrp-trigger__sep">{{ t('to') }}</span>
        <span class="adrp-trigger__date">{{ value[1] }}</span>
      </template>
    </div>

    <!-- 面板 -->
    <div v-show="visible" class="adrp-panel">
      <div class="adrp-panel__body">
        <div class="adrp-side">
          <ul class="adrp-preset">
            <li
              v-for="item in presets"
              :key="item.key"
              :class="{ 'is-active': activePreset === item.key && activeTarget === 'main' }"
              @click="selectPreset(item.key)"
            >{{ item.label }}</li>
          </ul>
          <div class="adrp-side__divider"></div>
          <div class="adrp-compare">
            <div class="adrp-compare__head">
              <span class="adrp-compare__title">{{ t('compare') }}</span>
              <el-switch v-model="tempCompare.enabled" @change="onCompareToggle"></el-switch>
            </div>
            <ul v-show="tempCompare.enabled" class="adrp-preset adrp-preset--sub">
              <li
                :class="{ 'is-active': tempCompare.mode === 'prev' }"
                @click="selectCompareMode('prev')"
              >{{ t('prevPeriod') }}</li>
              <li
                :class="{ 'is-active': tempCompare.mode === 'custom' }"
                @click="selectCompareMode('custom')"
              >{{ t('custom') }}</li>
            </ul>
          </div>
        </div>

        <!-- 双月日历 -->
        <div class="adrp-main">
          <div class="adrp-header">
            <div class="adrp-header__month">
              <span class="adrp-nav adrp-nav--prev-year" @click="shiftMonth(-12)">&laquo;</span>
              <span class="adrp-nav adrp-nav--prev-month" @click="shiftMonth(-1)">&lsaquo;</span>
              <span class="adrp-header__label">{{ months[0].label }}</span>
            </div>
            <div class="adrp-header__month">
              <span class="adrp-header__label">{{ months[1].label }}</span>
              <span
                class="adrp-nav adrp-nav--next-month"
                :class="{ 'is-disabled': !canShiftForward(1) }"
                @click="shiftMonth(1)"
              >&rsaquo;</span>
              <span
                class="adrp-nav adrp-nav--next-year"
                :class="{ 'is-disabled': !canShiftForward(12) }"
                @click="shiftMonth(12)"
              >&raquo;</span>
            </div>
          </div>
          <div class="adrp-months" @mouseleave="hoverDate = ''">
            <div class="adrp-month" v-for="(month, mi) in months" :key="mi">
              <div class="adrp-week">
                <span v-for="(w, wi) in weekdays" :key="wi">{{ w }}</span>
              </div>
              <div class="adrp-grid">
                <div
                  v-for="cell in month.cells"
                  :key="cell.str"
                  class="adrp-cell"
                  :class="cellClass(cell)"
                  @click="onCellClick(cell)"
                  @mouseenter="onCellHover(cell)"
                >
                  <span class="adrp-cell__text">{{ cell.day }}</span>
                </div>
              </div>
            </div>
          </div>
          <div class="adrp-summary">
            <span class="adrp-summary__item">
              <i class="adrp-summary__dot"></i>
              <span class="adrp-summary__label">{{ t('curPeriod') }}</span>
              <span class="adrp-summary__range">{{ rangeText(displayMain) }}</span>
            </span>
            <!-- 对比期 -->
            <span v-if="tempCompare.enabled" class="adrp-summary__item is-compare">
              <i class="adrp-summary__dot"></i>
              <span class="adrp-summary__label">{{ t('cycPeriod') }}</span>
              <span class="adrp-summary__range">{{ rangeText(previewCompare || displayCompare) }}</span>
            </span>
          </div>
        </div>
      </div>

      <div class="adrp-footer">
        <span class="adrp-footer__tip">{{ panelTip }}</span>
        <el-button type="primary" @click="apply">{{ t('apply') }}</el-button>
      </div>
    </div>
  </div>
</template>

<script>
function pad(n) {
  return n < 10 ? '0' + n : '' + n
}
function toStr(date) {
  return date.getFullYear() + '-' + pad(date.getMonth() + 1) + '-' + pad(date.getDate())
}
function toDate(str) {
  const parts = String(str).split('-')
  return new Date(Number(parts[0]), Number(parts[1]) - 1, Number(parts[2]))
}
function addDays(str, n) {
  const d = toDate(str)
  d.setDate(d.getDate() + n)
  return toStr(d)
}
function diffDays(a, b) {
  return Math.round((toDate(b).getTime() - toDate(a).getTime()) / 86400000)
}

const PRESET_KEYS = ['today', 'yesterday', 'last7', 'last30', 'thisMonth']
const PRESET_I18N = {
  today: 'today',
  yesterday: 'yesterday',
  last7: 'last7Days',
  last30: 'last30Days',
  thisMonth: 'thisMonth',
  custom: 'custom'
}

export default {
  name: 'AbroadDateRangePicker',
  props: {
    value: {
      type: Array,
      default: () => []
    },
    compare: {
      type: Object,
      default: () => ({ enabled: false, mode: 'prev', start: '', end: '' })
    },
    width: {
      type: [String, Number],
      default: 302
    },
    // 只用于 :preset.sync 的回写，组件内部不读它
    preset: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      today: toStr(new Date()),
      visible: false,
      // 面板内的临时状态，只有点「应用」才会提交给父组件
      tempStart: '',
      tempEnd: '',
      tempCompare: { enabled: false, mode: 'prev', start: '', end: '' },
      // 'main' = 日历点击作用于主区间，'compare' = 作用于对比区间
      activeTarget: 'main',
      activePreset: 'custom',
      selecting: false,
      hoverDate: '',
      leftIdx: 0 // 左侧月份 = year * 12 + monthIndex
    }
  },
  computed: {
    rootStyle() {
      return { width: typeof this.width === 'number' ? this.width + 'px' : this.width }
    },
    weekdays() {
      return String(this.t('weekdays')).split(',')
    },
    monthNames() {
      return String(this.t('monthNames')).split(',')
    },
    todayIdx() {
      const d = toDate(this.today)
      return d.getFullYear() * 12 + d.getMonth()
    },
    presets() {
      return PRESET_KEYS.concat('custom').map(key => ({
        key,
        label: this.t(PRESET_I18N[key])
      }))
    },
    // 主区间长度（天），对比区间长度锁定为它
    mainLength() {
      if (!this.tempStart || !this.tempEnd) return 1
      return diffDays(this.tempStart, this.tempEnd) + 1
    },
    // 日历上实际高亮的主区间（选择过程中跟随 hover 预览）
    displayMain() {
      if (!this.tempStart) return null
      if (this.selecting && this.activeTarget === 'main') {
        const h = this.hoverDate || this.tempStart
        return h < this.tempStart
          ? { start: h, end: this.tempStart }
          : { start: this.tempStart, end: h }
      }
      if (!this.tempEnd) return null
      return { start: this.tempStart, end: this.tempEnd }
    },
    displayCompare() {
      if (!this.tempCompare.enabled) return null
      if (!this.tempCompare.start || !this.tempCompare.end) return null
      return { start: this.tempCompare.start, end: this.tempCompare.end }
    },
    // 对比自定义模式下鼠标悬停的预览框：从悬停这天往后数 mainLength 天，画成灰框
    previewCompare() {
      if (this.activeTarget !== 'compare' || !this.hoverDate) return null
      return { start: this.hoverDate, end: addDays(this.hoverDate, this.mainLength - 1) }
    },
    months() {
      return [this.leftIdx, this.leftIdx + 1].map(idx => ({
        idx,
        label: this.monthLabel(idx),
        cells: this.buildCells(idx)
      }))
    },
    hasValue() {
      const v = Array.isArray(this.value) ? this.value : []
      return !!(v[0] && v[1])
    },
    hasCompare() {
      const c = this.compare || {}
      return !!(c.enabled && c.start && c.end)
    },
    // 鼠标悬停时给出完整信息（含对比区间），不占用触发框的版面
    triggerTitle() {
      if (!this.hasValue) return ''
      let text = this.value[0] + ' ' + this.t('to') + ' ' + this.value[1]
      if (this.hasCompare) {
        text += String(this.t('compareSuffixPattern'))
          .replace('%s', this.compare.start)
          .replace('%e', this.compare.end)
      }
      return text
    },
    panelTip() {
      if (this.activeTarget === 'compare') return this.t('tipCompare')
      if (this.selecting) return this.t('tipSelectEnd')
      return ''
    }
  },
  watch: {
    value() {
      // 面板未打开时跟随父组件的值变化
      if (!this.visible) this.syncFromProps()
    },
    // 主面板选中的预设（今天 / 近7天 …）同步给父组件。抛的是 i18n 的键名而不是文案
    activePreset(val) {
      this.$emit('update:preset', PRESET_I18N[val] || 'custom')
    }
  },
  mounted() {
    this.syncFromProps()
    document.addEventListener('mousedown', this.onDocMouseDown)
  },
  beforeDestroy() {
    document.removeEventListener('mousedown', this.onDocMouseDown)
  },
  methods: {
    t(key) {
      return this.$t('dateRangePicker.' + key)
    },
    rangeText(range) {
      if (!range || !range.start || !range.end) return '-'
      if (range.start === range.end) return range.start
      return range.start + ' ' + this.t('to') + ' ' + range.end
    },
    // 面板开关
    togglePanel() {
      if (this.visible) {
        this.closePanel()
      } else {
        this.syncFromProps()
        this.visible = true
      }
    },
    closePanel() {
      this.visible = false
      // 关闭不提交，回滚成 props 的值
      this.syncFromProps()
    },
    onDocMouseDown(e) {
      if (!this.visible) return
      if (this.$refs.root && this.$refs.root.contains(e.target)) return
      this.closePanel()
    },
    syncFromProps() {
      const v = Array.isArray(this.value) ? this.value : []
      this.tempStart = v[0] || this.today
      this.tempEnd = v[1] || this.tempStart
      const c = this.compare || {}
      this.tempCompare = {
        enabled: !!c.enabled,
        mode: c.mode || 'prev',
        start: c.start || '',
        end: c.end || ''
      }
      this.activeTarget = 'main'
      this.activePreset = this.matchPreset(this.tempStart, this.tempEnd)
      this.selecting = false
      this.hoverDate = ''
      this.syncMonths()
      if (this.tempCompare.enabled && (!this.tempCompare.start || !this.tempCompare.end)) {
        this.recomputeCompare()
      }
    },
    presetRange(key) {
      const t = this.today
      switch (key) {
        case 'today':
          return [t, t]
        case 'yesterday': {
          const y = addDays(t, -1)
          return [y, y]
        }
        case 'last7':
          return [addDays(t, -6), t] 
        case 'last30':
          return [addDays(t, -29), t]
        case 'thisMonth': {
          const d = toDate(t)
          return [toStr(new Date(d.getFullYear(), d.getMonth(), 1)), t]
        }
        default:
          return null
      }
    },
    matchPreset(start, end) {
      for (let i = 0; i < PRESET_KEYS.length; i++) {
        const r = this.presetRange(PRESET_KEYS[i])
        if (r && r[0] === start && r[1] === end) return PRESET_KEYS[i]
      }
      return 'custom'
    },
    selectPreset(key) {
      this.activeTarget = 'main'
      this.activePreset = key
      this.selecting = false
      this.hoverDate = ''
      const r = this.presetRange(key)
      if (r) {
        this.tempStart = r[0]
        this.tempEnd = r[1]
        this.syncMonths()
        this.recomputeCompare()
      }
    },
    onCompareToggle(val) {
      this.activeTarget = 'main'
      this.hoverDate = ''
      if (val) {
        this.tempCompare.mode = 'prev'
        this.recomputeCompare()
      } else {
        this.tempCompare.start = ''
        this.tempCompare.end = ''
      }
    },
    selectCompareMode(mode) {
      this.tempCompare.mode = mode
      this.selecting = false
      this.hoverDate = ''
      this.activeTarget = mode === 'custom' ? 'compare' : 'main'
      this.recomputeCompare()
    },
    recomputeCompare() {
      if (!this.tempCompare.enabled) return
      const len = this.mainLength
      if (this.tempCompare.mode === 'prev') {
        // 等长且紧邻主区间之前
        const end = addDays(this.tempStart, -1)
        this.tempCompare.start = addDays(end, -(len - 1))
        this.tempCompare.end = end
        return
      }
      // 自定义：保留起点，按新长度重算，越过今天则回收到 [today-len+1, today]
      let start = this.tempCompare.start || addDays(this.tempStart, -len)
      let end = addDays(start, len - 1)
      if (end > this.today) {
        end = this.today
        start = addDays(end, -(len - 1))
      }
      this.tempCompare.start = start
      this.tempCompare.end = end
    },
    syncMonths() {
      const d = toDate(this.tempStart || this.today)
      let idx = d.getFullYear() * 12 + d.getMonth()
      // 右侧月份不允许超过当月，所以左侧最多到上个月
      if (idx > this.todayIdx - 1) idx = this.todayIdx - 1
      this.leftIdx = idx
    },
    monthLabel(idx) {
      const y = Math.floor(idx / 12)
      const m = idx % 12
      return String(this.t('monthLabelPattern'))
        .replace('%y', y)
        .replace('%m', this.monthNames[m] || m + 1)
    },
    canShiftForward(n) {
      return this.leftIdx + n <= this.todayIdx - 1
    },
    shiftMonth(n) {
      if (n > 0 && !this.canShiftForward(n)) return
      this.leftIdx += n
    },

    isDisabled(str) {
      if (str > this.today) return true
      if (this.activeTarget === 'compare') {
        // 对比区间长度锁定，会越过今天的起点不可选
        return addDays(str, this.mainLength - 1) > this.today
      }
      return false
    },
    buildCells(idx) {
      const y = Math.floor(idx / 12)
      const m = idx % 12
      const offset = new Date(y, m, 1).getDay() // 0 = 周日
      const main = this.displayMain
      const cmp = this.displayCompare
      const pre = this.previewCompare
      const cells = []
      for (let i = 0; i < 42; i++) {
        const d = new Date(y, m, 1 - offset + i)
        const str = toStr(d)
        // 非本月的日期只作占位展示：不参与高亮、不可点，避免同一天在两个月里同时被画出来
        const inCurrentMonth = d.getMonth() === m
        const inMain = inCurrentMonth && !!(main && str >= main.start && str <= main.end)
        const inCompare = inCurrentMonth && !!(cmp && str >= cmp.start && str <= cmp.end)
        const inPreview = inCurrentMonth && !!(pre && str >= pre.start && str <= pre.end)
        cells.push({
          str,
          day: d.getDate(),
          inCurrentMonth,
          isRowStart: i % 7 === 0,
          isRowEnd: i % 7 === 6,
          isToday: inCurrentMonth && str === this.today,
          disabled: !inCurrentMonth || this.isDisabled(str),
          inMain,
          mainStart: inMain && str === main.start,
          mainEnd: inMain && str === main.end,
          inCompare,
          compareStart: inCompare && str === cmp.start,
          compareEnd: inCompare && str === cmp.end,
          inPreview,
          previewStart: inPreview && str === pre.start,
          previewEnd: inPreview && str === pre.end
        })
      }
      return cells
    },
    cellClass(cell) {
      let roundLeft = false
      let roundRight = false
      if (cell.inMain) {
        roundLeft = cell.mainStart || cell.isRowStart
        roundRight = cell.mainEnd || cell.isRowEnd
      } else if (cell.inCompare) {
        roundLeft = cell.compareStart || cell.isRowStart
        roundRight = cell.compareEnd || cell.isRowEnd
      } else if (cell.inPreview) {
        roundLeft = cell.previewStart || cell.isRowStart
        roundRight = cell.previewEnd || cell.isRowEnd
      }
      return {
        'is-other-month': !cell.inCurrentMonth,
        'is-disabled': cell.disabled,
        'is-today': cell.isToday,
        'in-compare': cell.inCompare,
        'is-compare-edge': cell.compareStart || cell.compareEnd,
        'in-main': cell.inMain,
        'is-main-edge': cell.mainStart || cell.mainEnd,
        'is-single-main': cell.mainStart && cell.mainEnd,
        'in-preview': cell.inPreview,
        'is-preview-edge': cell.previewStart || cell.previewEnd,
        'is-round-left': roundLeft,
        'is-round-right': roundRight
      }
    },
    onCellHover(cell) {
      this.hoverDate = cell.disabled ? '' : cell.str
    },
    onCellClick(cell) {
      if (cell.disabled) return
      if (this.activeTarget === 'compare') {
        this.tempCompare.mode = 'custom'
        this.tempCompare.start = cell.str
        this.tempCompare.end = addDays(cell.str, this.mainLength - 1)
        return
      }
      if (!this.selecting) {
        this.tempStart = cell.str
        this.tempEnd = cell.str
        this.selecting = true
        this.activePreset = 'custom'
        return
      }
      let start = this.tempStart
      let end = cell.str
      if (end < start) {
        const tmp = start
        start = end
        end = tmp
      }
      this.tempStart = start
      this.tempEnd = end
      this.selecting = false
      this.activePreset = this.matchPreset(start, end)
      this.recomputeCompare()
    },
    // 提交
    apply() {
      if (!this.tempStart || !this.tempEnd) return
      if (this.selecting) {
        // 只点了一次就应用，按单天处理
        this.tempEnd = this.tempStart
        this.selecting = false
        this.recomputeCompare()
      }
      const payload = {
        start: this.tempStart,
        end: this.tempEnd,
        compare: Object.assign({}, this.tempCompare)
      }
      this.$emit('input', [this.tempStart, this.tempEnd])
      this.$emit('update:compare', Object.assign({}, this.tempCompare))
      this.$emit('apply', payload)
      this.visible = false
    }
  }
}
</script>

<style lang="scss" scoped>
$main: #3361ff;
$main-band: #ebf2fe;
$cmp: #ff8a29;
$cmp-band: #fff1e3;
$text: #1f2329;
$text-sub: #4e5969;
$text-weak: #8f959e;
$gray-line: #c9cdd4;
$line: #f0f1f3;
$cell-w: 38px;
$cell-h: 36px;

.abroad-date-range-picker {
  position: relative;
  display: inline-block;
  vertical-align: middle;
  font-size: 14px; // 与页面 label / el-select 一致
}

/* 触发器：各项数值照 element 原生 el-input。
   注意 main.js 里全局 Vue.use(ElementUI, { size: 'small' })，所以是 32px 而不是默认的 40px */
.adrp-trigger {
  display: flex;
  align-items: center;
  width: 100%;
  height: 32px;
  padding: 0 10px;
  box-sizing: border-box;
  border: 1px solid #dcdfe6;
  border-radius: 4px;
  background: #fff;
  color: #606266;
  cursor: pointer;
  transition: border-color 0.2s cubic-bezier(0.645, 0.045, 0.355, 1);

  &:hover {
    border-color: #c0c4cc;
  }

  &.is-active {
    border-color: $main;
  }
}
.adrp-trigger__icon {
  flex: 0 0 auto;
  font-size: 14px;
  color: #c0c4cc;
}
/* 起止日期各占一半宽度居中、「至」固定在正中间 —— 与原生 el-range-editor 一致 */
.adrp-trigger__date {
  flex: 1;
  text-align: center;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
}
.adrp-trigger__sep {
  flex: 0 0 24px;
  text-align: center;
  color: #303133;
}
.adrp-trigger__placeholder {
  flex: 1;
  padding-left: 8px;
  color: #c0c4cc;
}

/* 面板 */
.adrp-panel {
  position: absolute;
  top: calc(100% + 6px);
  left: 0;
  z-index: 2100;
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 6px 24px rgba(31, 35, 41, 0.1), 0 2px 6px rgba(31, 35, 41, 0.04);
  cursor: default;
}
.adrp-panel__body {
  display: flex;
}

/* 左栏：原型里左右两栏之间没有竖分隔线，各项高度按日历行高排，两栏自然等高 */
.adrp-side {
  width: 120px;
  padding: 12px 0;
  box-sizing: border-box;
}
.adrp-side__divider {
  height: 1px;
  margin: 6px 12px;
  background: $line;
}
.adrp-preset {
  margin: 0;
  padding: 0;
  list-style: none;

  li {
    margin: 0 8px;
    padding: 0 10px;
    height: 36px;
    line-height: 36px;
    border-radius: 8px;
    color: $text-sub;
    cursor: pointer;
    white-space: nowrap;
    transition: background-color 0.15s;

    &:hover {
      background: #f5f6f7;
    }

    &.is-active {
      background: #eaf2fe;
      color: $main;
      font-weight: 500;
    }
  }
}
/* 对比的两个子项在原型里缩进更深一级 */
.adrp-preset--sub li {
  padding-left: 20px;
}
.adrp-compare__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 36px;
  padding: 0 12px 0 18px;
}
.adrp-compare__title {
  color: $text;
}

/* 右栏。竖着排是为了让回显能用 auto 外边距压到栏底（见 .adrp-summary）。
   上边距比左栏多 20px：日历整体往下坐，月份标题不再和「今天」齐平，
   两栏的起手位置错开，视线更容易落到日历上。
   这个值的上限是 41px —— 再大右栏就比左栏高了，回显不再贴着分隔线，
   面板整体跟着变高 */
.adrp-main {
  display: flex;
  flex-direction: column;
  padding: 32px 16px 12px;
}
.adrp-summary {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  margin-top: auto;
  padding-top: 12px;
  font-size: 13px;
  line-height: 20px;
}
/* 颜色挂在整项上，圆点用 currentColor 取，改一处就够 */
.adrp-summary__item {
  display: flex;
  align-items: center;
  margin-right: 24px;
  color: $main;

  &.is-compare {
    color: $cmp;
  }
  &:last-child {
    margin-right: 0;
  }
}
.adrp-summary__dot {
  flex: 0 0 auto;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: currentColor;
}
.adrp-summary__label {
  margin: 0 6px;
  color: $text-sub;
}
.adrp-summary__range {
  color: $text;
  font-weight: 600;
}
.adrp-header {
  display: flex;
  height: 30px;
}
/* 每段宽度、间距都和 .adrp-month 一致，表头才和日历对得上 */
.adrp-header__month {
  position: relative;
  display: flex;
  align-items: center;
  width: $cell-w * 7;

  & + & {
    margin-left: 20px;
  }
}
.adrp-header__label {
  flex: 1;
  text-align: center;
  font-size: 16px;
  font-weight: 600;
  color: $text;
}
/* 箭头绝对定位：留在流里的话会占掉宽度，把 flex:1 的月份文案挤偏。
   它们是定位元素，绘制在文案之上，点击不受影响 */
.adrp-nav {
  position: absolute;
  top: 0;
  width: 24px;
  height: 30px;
  line-height: 30px;
  text-align: center;
  font-size: 16px;
  color: $text-weak;
  cursor: pointer;
  user-select: none;

  &:hover {
    color: $main;
  }

  &.is-disabled {
    color: #dee0e3;
    cursor: not-allowed;
  }
}
.adrp-nav--prev-year {
  left: 0;
}
.adrp-nav--prev-month {
  left: 24px;
}
.adrp-nav--next-month {
  right: 24px;
}
.adrp-nav--next-year {
  right: 0;
}
.adrp-months {
  display: flex;
}
.adrp-month {
  width: $cell-w * 7;

  & + & {
    margin-left: 20px;
  }
}
.adrp-week {
  display: flex;

  span {
    width: $cell-w;
    height: 30px;
    line-height: 30px;
    text-align: center;
    color: $text-weak;
    font-size: 13px;
  }
}
.adrp-grid {
  display: flex;
  flex-wrap: wrap;
}

/* 底部：分隔线只从日历左边缘起（照原型），所以用伪元素画，不能用 border-top */
.adrp-footer {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  height: 52px;
  padding: 0 16px 0 136px;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 136px;
    right: 16px;
    height: 1px;
    background: $line;
  }
}
.adrp-footer__tip {
  flex: 1;
  padding-right: 12px;
  color: $text-weak;
  font-size: 13px;
}
/* 全局 main.css 把 .el-button--small 的高度锁成 32px、字号提到 14px，
   再叠上下各 10px 的 padding，内容盒只剩 10px，装不下 14px 的行盒，
   文字就被顶到中线下面 2px。改成 flex 居中，高度和字号谁变都不会跑偏 */
.adrp-footer /deep/ .el-button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0 20px;
  border-radius: 8px;
}

/* 单元格：色带就是格子自己的背景色，相邻格子拼成连续的一条 */
.adrp-cell {
  width: $cell-w;
  height: $cell-h;
  box-sizing: border-box;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  color: $text;
  cursor: pointer;

  &.is-other-month {
    color: $gray-line;
  }

  &.is-disabled {
    color: $gray-line;
    cursor: not-allowed;
  }
}
.adrp-cell__text {
  width: 32px;
  height: 32px;
  line-height: 32px;
  text-align: center;
  border-radius: 50%;
}
.adrp-cell.is-today .adrp-cell__text {
  font-weight: 600;
  color: $main;
}
.adrp-cell:not(.is-disabled):not(.in-main):not(.in-compare):hover .adrp-cell__text {
  background: #f5f6f7;
}

/* 色带在真正的区间端点、以及每行的首尾收成圆角。
   哪条带子说话由 cellClass() 定，一格只有一种圆角，连续的带子中间不会被切出缺口 */
.adrp-cell.is-round-left {
  border-top-left-radius: $cell-h / 2;
  border-bottom-left-radius: $cell-h / 2;
}
.adrp-cell.is-round-right {
  border-top-right-radius: $cell-h / 2;
  border-bottom-right-radius: $cell-h / 2;
}

/* 对比自定义模式下的 hover 预览：最浅的一层灰底 + 端点上的灰圆环 */
.adrp-cell.in-preview {
  background-color: #f2f3f5;
}
.adrp-cell.is-preview-edge .adrp-cell__text {
  box-shadow: inset 0 0 0 1px $gray-line;
}

/* 对比区间：淡橙色带 + 端点实心橙圆点 */
.adrp-cell.in-compare {
  background-color: $cmp-band;
}

/* 主区间：淡蓝色带 + 端点实心蓝圆点。写在对比之后，重叠处蓝底盖住淡橙 */
.adrp-cell.in-main {
  background-color: $main-band;
}
/* 单天区间不画色带，只留端点圆点 */
.adrp-cell.is-single-main {
  background-color: transparent;
}

/* 端点圆点：主区间蓝、对比区间橙。同一天既是主端点又是对比端点时以主区间为准 */
.adrp-cell.is-compare-edge .adrp-cell__text {
  background: $cmp;
  color: #fff;
  font-weight: 500;
}
.adrp-cell.is-main-edge .adrp-cell__text {
  background: $main;
  color: #fff;
  font-weight: 500;
}
</style>
